import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageShell, ShellSection } from "./PageShell";
import { StatusBadge } from "../components/common";
import { Reveal } from "../components/Reveal";
import { MEDIA_IP, LANES } from "../data/content";
import { usePageMeta } from "../hooks/usePageMeta";

export const RetailPage = () => {
    const lane = LANES.retail;
    usePageMeta("THG Retail — Titan Haven Group LLC", lane.lede);
    return (
        <PageShell testId="retail-page" overline={lane.overline} title={lane.title} lede={lane.lede}>
            <ShellSection>
                <Reveal className="flex flex-wrap items-center gap-3">
                    <StatusBadge testId="retail-status-badge">{lane.status}</StatusBadge>
                    <span className="text-xs tracking-[0.15em] uppercase text-thg-tertiary">
                        Permanent destination: thgretail.com
                    </span>
                </Reveal>
                <Reveal delay={0.1}>
                    <p className="mt-6 text-sm md:text-base leading-relaxed text-thg-secondary max-w-3xl">
                        {lane.statusText}
                    </p>
                </Reveal>
            </ShellSection>
            <ShellSection>
                <Reveal>
                    <h2 className="font-display text-2xl md:text-3xl font-medium text-white">Brands &amp; products</h2>
                </Reveal>
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 border-t border-l border-thg-border">
                    {lane.brands.map((b, i) => (
                        <Reveal key={b.name} delay={i * 0.05} className="border-b border-r border-thg-border p-8" data-testid={`retail-brand-${i}`}>
                            <h3 className="font-display text-xl font-medium text-white">{b.name}</h3>
                            <p className="mt-3 text-sm leading-relaxed text-thg-secondary">{b.text}</p>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.1}>
                    <p className="mt-6 text-xs leading-relaxed text-thg-tertiary max-w-2xl">{lane.imageryNote}</p>
                </Reveal>
            </ShellSection>
        </PageShell>
    );
};

export const MediaPage = () => {
    const lane = LANES.media;
    usePageMeta("Sports / Media / IP — Titan Haven Group LLC", lane.lede);
    return (
        <PageShell testId="media-page" overline={lane.overline} title={lane.title} lede={lane.lede}>
            <ShellSection>
                <div className="border-t border-thg-border">
                    {MEDIA_IP.map((e, i) => (
                        <Reveal
                            key={e.name}
                            delay={i * 0.05}
                            className="grid grid-cols-1 md:grid-cols-12 gap-3 py-8 border-b border-thg-border"
                            data-testid={`media-entity-${i}`}
                        >
                            <div className="md:col-span-4">
                                <h3 className="font-display text-xl font-medium text-white">{e.name}</h3>
                                {e.legalName && e.legalName !== e.name && (
                                    <p className="mt-1 text-xs text-thg-tertiary">{e.legalName}</p>
                                )}
                                <p className="mt-1 text-xs text-thg-tertiary">{e.relationship}</p>
                            </div>
                            <p className="md:col-span-5 text-sm leading-relaxed text-thg-secondary">{e.summary}</p>
                            <div className="md:col-span-3 flex md:justify-end items-center gap-4 flex-wrap">
                                <StatusBadge>{e.status}</StatusBadge>
                                {e.to && (
                                    <Link
                                        to={e.to}
                                        data-testid="media-turftalk-view"
                                        className="inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase text-thg-secondary hover:text-white transition-colors duration-300"
                                    >
                                        View <ArrowUpRight size={13} />
                                    </Link>
                                )}
                                {e.links?.map((l) => (
                                    <a
                                        key={l.href}
                                        href={l.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={l.testId}
                                        className="inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase text-thg-secondary hover:text-white transition-colors duration-300"
                                        aria-label={`${e.name} ${l.label} — opens in a new tab`}
                                    >
                                        {l.label} <ArrowUpRight size={13} />
                                    </a>
                                ))}
                            </div>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.1}>
                    <p className="mt-6 text-xs leading-relaxed text-thg-tertiary max-w-2xl">
                        Relationship labels are deliberate: THG-owned products, affiliated entities, and properties
                        developed within affiliated entities are different structures, and they are presented
                        differently.
                    </p>
                    <Link
                        to="/portfolio"
                        data-testid="media-back-link"
                        className="mt-6 inline-flex items-center gap-2 text-sm text-thg-secondary hover:text-white transition-colors duration-300"
                    >
                        Back to portfolio
                    </Link>
                </Reveal>
            </ShellSection>
        </PageShell>
    );
};
